import { useState } from 'react';
import { api } from '../lib/api';

interface Props {
  taskId: string;
  taskTitle: string;
  onComplete: () => void;
  onClose: () => void;
}

const today = () => new Date().toISOString().slice(0, 10);

export function TaskCompleteModal({ taskId, taskTitle, onComplete, onClose }: Props) {
  const [completedDate, setCompletedDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [cost, setCost] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const notesLeft = 1000 - notes.length;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const body: Record<string, unknown> = { completed_date: completedDate };
      if (notes.trim()) body.notes = notes.trim();
      const parsed = parseFloat(cost);
      if (cost !== '' && !isNaN(parsed) && parsed >= 0) body.cost = parsed;

      await api.put(`/tasks/${taskId}/complete`, body);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#fff', borderRadius: '1rem', padding: '1.5rem',
          width: '100%', maxWidth: '28rem', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#111' }}>Mark Complete</h2>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#6b7280', maxWidth: '20rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {taskTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#9ca3af', padding: '0.25rem' }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.375rem' }}>
              Completion date
            </label>
            <input
              type="date"
              value={completedDate}
              max={today()}
              onChange={(e) => setCompletedDate(e.target.value)}
              required
              style={{
                width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                border: '1px solid #d1d5db', fontSize: '0.9rem', boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.375rem' }}>
              Notes <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
              placeholder="What did you do? Any observations or follow-ups?"
              rows={3}
              style={{
                width: '100%', padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                border: '1px solid #d1d5db', fontSize: '0.875rem',
                resize: 'vertical', boxSizing: 'border-box', outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            <div style={{ textAlign: 'right', fontSize: '0.75rem', color: notesLeft < 50 ? '#dc2626' : '#9ca3af', marginTop: '0.25rem' }}>
              {notesLeft} characters left
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.375rem' }}>
              Cost <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: '0.9rem' }}>$</span>
              <input
                type="number"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                min="0"
                step="0.01"
                placeholder="0.00"
                style={{
                  width: '100%', padding: '0.5rem 0.75rem 0.5rem 1.5rem',
                  borderRadius: '0.5rem', border: '1px solid #d1d5db',
                  fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none',
                }}
              />
            </div>
          </div>

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.625rem 0.75rem', fontSize: '0.85rem', color: '#dc2626' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.25rem' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1, padding: '0.625rem', borderRadius: '0.5rem',
                border: '1px solid #d1d5db', background: '#fff',
                fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', color: '#374151',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                flex: 2, padding: '0.625rem', borderRadius: '0.5rem',
                border: 'none', background: saving ? '#a3e635' : '#16a34a',
                fontSize: '0.9rem', fontWeight: 600, cursor: saving ? 'default' : 'pointer',
                color: '#fff', transition: 'background 0.15s',
              }}
            >
              {saving ? 'Saving…' : 'Mark Complete'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
